import React from 'react';
import { useExperiments } from '@wix/wix-experiments-react';
import { GlobalDesignActionLobbyItem } from '../types';
import {
  COMBINED_BORDER_PROPERTIES,
  getBorderProperty,
} from '../../../apis/services/editActionsServices/nativeActions/globalDesign/border/constants';
import { processStyleMetadataForLobby } from '../common/lobby/processStyleMetadataForLobby';
import { LobbyItemLayout } from '../common/lobby/LobbyItemLayout';
import BorderPreview from '../controls/GroupPreview/BorderPreview/BorderPreview';
import { useGroupsPreviewPS } from '../common/lobby/useGroupsPreviewPS';
import { compose } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/composer';
import { GD_WDS_EXPERIMENT } from '../experiments';

const BORDER_PROPERTY_CONFIG = [
  {
    resultKey: 'borderColor',
    mixedKey: 'isMixedBorderColor',
    atomicKeys: COMBINED_BORDER_PROPERTIES.map((direction) =>
      getBorderProperty(direction, 'Color'),
    ),
  },
  {
    resultKey: 'borderWidth',
    mixedKey: 'isMixedBorderWidth',
    atomicKeys: COMBINED_BORDER_PROPERTIES.map((direction) =>
      getBorderProperty(direction, 'Width'),
    ),
  },
  {
    resultKey: 'borderStyle',
    mixedKey: 'isMixedBorderStyle',
    atomicKeys: COMBINED_BORDER_PROPERTIES.map((direction) =>
      getBorderProperty(direction, 'Style'),
    ),
  },
] as const;

const BorderLobbyItem: GlobalDesignActionLobbyItem = (props) => {
  const { experiments } = useExperiments({ readOnly: true });
  const isWDS = experiments.enabled(GD_WDS_EXPERIMENT);

  const renderGroupPreviewPS = useGroupsPreviewPS(
    props.actionName,
    props.group,
  );

  const processedStyleMetadata = processStyleMetadataForLobby(
    props.group.engine,
    BORDER_PROPERTY_CONFIG,
  );

  if (!isWDS) {
    const isMixed =
      processedStyleMetadata.isMixedBorderColor ||
      processedStyleMetadata.isMixedBorderStyle ||
      processedStyleMetadata.isMixedBorderWidth;

    let value: string | string[];
    if (isMixed) {
      const combinations: string[] = [];
      for (const color of processedStyleMetadata.borderColor) {
        for (const width of processedStyleMetadata.borderWidth) {
          for (const style of processedStyleMetadata.borderStyle) {
            const composed =
              compose('border', {
                borderColor: color,
                borderWidth: width,
                borderStyle: style,
              }) || '';
            combinations.push(composed);
          }
        }
      }
      value = combinations;
    } else {
      value =
        compose('border', {
          borderColor: processedStyleMetadata.borderColor[0] ?? null,
          borderWidth: processedStyleMetadata.borderWidth[0] ?? null,
          borderStyle: processedStyleMetadata.borderStyle[0] ?? null,
        }) || '';
    }

    return (
      renderGroupPreviewPS({
        value,
        emptyPreviewLabel:
          props.group.displayName.group || props.group.displayName.component,
        isMixedBorderColor: processedStyleMetadata.isMixedBorderColor,
        isMixedBorderStyle: processedStyleMetadata.isMixedBorderStyle,
        isMixedBorderWidth: processedStyleMetadata.isMixedBorderWidth,
        onClick: props.onSelect,
      }) ?? null
    );
  }

  return (
    <LobbyItemLayout
      actionName={props.actionName}
      group={props.group}
      onSelect={props.onSelect}
    >
      <BorderPreview
        colors={processedStyleMetadata.borderColor}
        width={processedStyleMetadata.borderWidth[0]}
        styleKeyword={processedStyleMetadata.borderStyle[0]}
        isMixedWidth={processedStyleMetadata.isMixedBorderWidth}
        isMixedStyle={processedStyleMetadata.isMixedBorderStyle}
        emptyPreviewLabel={
          props.group.displayName.group || props.group.displayName.component
        }
      />
    </LobbyItemLayout>
  );
};

export default BorderLobbyItem;
